import * as THREE from "three";
import { drawItemIcon } from "../items/itemArt.js";
import { kaponHeightAt } from "./kaponTerrain.js";

/**
 * Client-side SKINNING channel — the visual twin of mining (`mine.ts`), but the target (a dead hog
 * corpse) lives in `HogController`, so this owns only the floating UI: a pixel progress bar + a
 * swinging skinning knife over the player. `start(hogId, durationMs)` begins the channel; `tick`
 * fills the bar + swings; when it completes it returns the hogId so the world sends a `Skin` message.
 */

function makeKnifeTexture(): THREE.CanvasTexture {
  const t = new THREE.CanvasTexture(drawItemIcon("skinning_knife"));
  t.magFilter = THREE.NearestFilter; t.minFilter = THREE.NearestFilter;
  return t;
}

export class SkinController {
  private channel: { hogId: string; start: number; duration: number } | null = null;
  private readonly ui = new THREE.Group();
  private readonly barTex: THREE.CanvasTexture;
  private readonly bar: THREE.Sprite;
  private readonly knife: THREE.Sprite;
  private readonly barCanvas: HTMLCanvasElement;

  constructor(private readonly scene: THREE.Scene) {
    this.barCanvas = document.createElement("canvas");
    this.barCanvas.width = 48; this.barCanvas.height = 10;
    this.barTex = new THREE.CanvasTexture(this.barCanvas);
    this.barTex.magFilter = THREE.NearestFilter; this.barTex.minFilter = THREE.NearestFilter;
    // Same depth treatment as the player billboard: alphaTest + depthWrite so buildings OCCLUDE these
    // (no seeing them through walls) and they write near depth, staying sharp under the world DoF.
    this.bar = new THREE.Sprite(new THREE.SpriteMaterial({ map: this.barTex, transparent: true, alphaTest: 0.5, depthWrite: true }));
    this.bar.scale.set(0.5, 0.1, 1); this.bar.position.y = 1.5;
    this.knife = new THREE.Sprite(new THREE.SpriteMaterial({ map: makeKnifeTexture(), transparent: true, alphaTest: 0.5, depthWrite: true }));
    this.knife.scale.set(0.34, 0.34, 1); this.knife.position.set(0, 1.72, 0);
    this.knife.center.set(0.3, 0.2);
    this.ui.add(this.bar, this.knife);
    this.ui.visible = false;
    this.scene.add(this.ui);
  }

  isSkinning(): boolean { return this.channel != null; }

  start(hogId: string, durationMs: number, nowMs: number): void {
    this.channel = { hogId, start: nowMs, duration: durationMs };
    this.ui.visible = true;
  }

  cancel(): void { this.channel = null; this.ui.visible = false; }

  /** Per-frame: move the UI over the player, fill the bar, swing the knife. Returns the hogId when
   *  the channel completes (the caller sends `Skin` + the channel ends), else null. */
  tick(playerX: number, playerZ: number, nowMs: number): string | null {
    if (!this.channel) return null;
    this.ui.position.set(playerX, kaponHeightAt(playerX, playerZ), playerZ);
    const t = Math.min(1, (nowMs - this.channel.start) / this.channel.duration);
    this.drawBar(t);
    // Short back-and-forth slicing motion.
    this.knife.material.rotation = -0.5 + Math.sin(nowMs / 90) * 0.5;
    if (t >= 1) { const id = this.channel.hogId; this.cancel(); return id; }
    return null;
  }

  private drawBar(t: number): void {
    const ctx = this.barCanvas.getContext("2d")!;
    const W = this.barCanvas.width, H = this.barCanvas.height;
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = "#05000a"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#2a1b44"; ctx.fillRect(1, 1, W - 2, H - 2);
    ctx.fillStyle = "#e0a14a"; ctx.fillRect(1, 1, Math.round((W - 2) * t), H - 2); // amber skinning fill
    ctx.fillStyle = "#1a1024";
    ctx.fillRect(0, 0, W, 1); ctx.fillRect(0, H - 1, W, 1); ctx.fillRect(0, 0, 1, H); ctx.fillRect(W - 1, 0, 1, H);
    this.barTex.needsUpdate = true;
  }

  dispose(): void {
    this.scene.remove(this.ui);
    (this.bar.material as THREE.SpriteMaterial).map?.dispose();
    (this.bar.material as THREE.SpriteMaterial).dispose();
    (this.knife.material as THREE.SpriteMaterial).map?.dispose();
    (this.knife.material as THREE.SpriteMaterial).dispose();
  }
}
